import { Combobox, Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { navigate } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import { Fragment } from 'react';
import { event } from '../../../lib/gtag';

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

export interface FilteredItem {
  title: string;
  body: string;
  href: string;
}

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  filteredItems: FilteredItem[];
}

const Search: React.FC<Props> = ({
  open,
  setOpen,
  query,
  setQuery,
  filteredItems,
}) => {
  const { t } = useTranslation();

  return (
    <Transition.Root
      show={open}
      as={Fragment}
      afterLeave={() => setQuery('')}
      appear
    >
      <Dialog as='div' className='relative z-50' onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-lsGray-900 bg-opacity-40 transition-opacity' />
        </Transition.Child>
        <div className='fixed inset-0 overflow-y-auto p-4 lg:p-28'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <Dialog.Panel className='ring-5 mx-auto max-w-xl transform divide-y divide-lsGray-200 overflow-hidden rounded-xl bg-white shadow-2xl ring-black ring-opacity-5 transition-all'>
              <Combobox
                value={query}
                onChange={(item: any) => {
                  event('search', {
                    search_term: query,
                    moved_page: item.href,
                  });
                  navigate(item.href);
                  setOpen(false);
                }}
              >
                <div className='relative'>
                  <MagnifyingGlassIcon
                    className='pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-black/20'
                    aria-hidden='true'
                  />
                  <Combobox.Input
                    className='h-12 w-full border-0 bg-transparent pl-11 pr-4 text-xs placeholder-lsGray-400 focus:ring-0'
                    placeholder={t('search.placeholder')}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                {filteredItems.length > 0 && (
                  <Combobox.Options
                    static
                    className='max-h-72 scroll-py-2 overflow-y-auto py-2'
                  >
                    {filteredItems.map((item, index) => (
                      <Combobox.Option
                        key={index}
                        value={item}
                        className={({ active }) =>
                          classNames(
                            'cursor-default select-none px-4 py-2 text-xs',
                            active && 'bg-lsGray-200',
                          )
                        }
                      >
                        {item.title}
                      </Combobox.Option>
                    ))}
                  </Combobox.Options>
                )}
                {query !== '' && filteredItems.length === 0 && (
                  <p className='p-4 text-xs'>{t('search.no_search_results')}</p>
                )}
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

Search.displayName = 'Search';

export default Search;
